import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Select, Button, AutoComplete, Modal, Input } from 'antd';
import { SearchOutlined, EnvironmentOutlined, AppstoreOutlined } from '@ant-design/icons';
import states from '../../services/states';
import lineOfBusiness from '../../services/lineOfBusiness';
import './index.css';
import { useSearchParams } from 'react-router-dom';
import { renderTextWithSearchHighlight } from '../../utils/highlightSearchTerm';
import { SEARCH_STATE_KEY, SEARCH_LOB_KEY, SEARCH_TYPE_KEY } from './../../constants/searchConstants';

const HOMEPAGE_HERO = {
  line1: 'Farm & Ranch',
  line2: 'Agency',
};

const SearchBar = ({ onSearch, showTitle = true, hero = HOMEPAGE_HERO, showQuickAccess = true }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statesList, setStatesList] = useState([]);
  const [allLobList, setAllLobList] = useState([]);

  const [selectedState, setSelectedState] = useState(() => {
    return searchParams.get('state') || localStorage.getItem(SEARCH_STATE_KEY) || null;
  });

  const [lobOptions, setLobOptions] = useState([]);
  const [selectedLob, setSelectedLob] = useState(() => {
    return searchParams.get('line_of_business') || localStorage.getItem(SEARCH_LOB_KEY) || '';
  });
  const [quickModal, setQuickModal] = useState(null);
  const [modalFilter, setModalFilter] = useState('');

  useEffect(() => {
    fetchStates();
    fetchAllLobs();
  }, []);

  useEffect(() => {
    const newState = searchParams.get('state');
    const newLob = searchParams.get('line_of_business');

    if (newState) localStorage.setItem(SEARCH_STATE_KEY, newState);
    if (newLob) localStorage.setItem(SEARCH_LOB_KEY, newLob);
    localStorage.setItem(SEARCH_TYPE_KEY, 'lob');

    setSelectedState(newState);
    setSelectedLob(newLob || '');
  }, [searchParams]);

  const fetchStates = async () => {
    try {
      const response = await states.get();
      const sorted = [...response].sort((a, b) => a.name.localeCompare(b.name));
      setStatesList(sorted.map((state) => ({
        value: state.name,
        label: state.name,
      })));
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchAllLobs = async () => {
    try {
      const response = await lineOfBusiness.get();
      const sorted = [...response].sort((a, b) => a.name.localeCompare(b.name));
      setAllLobList(sorted.map((lob) => lob.name));
    } catch (error) {
      console.error('Error fetching LOB list:', error);
    }
  };

  const fetchLobOptions = async (searchText) => {
    if (searchText.length <= 1) return;
    try {
      const response = await lineOfBusiness.get();
      const filteredOptions = response
        .filter(lob => lob.name.toLowerCase().includes(searchText.toLowerCase()))
        .map(lob => ({
          value: lob.name,
          label: lob.name
        }));
      setLobOptions(filteredOptions);
    } catch (error) {
      console.error('Error fetching LOB options:', error);
    }
  };

  const handleSearch = () => {
    if (selectedState) localStorage.setItem(SEARCH_STATE_KEY, selectedState);
    if (selectedLob) localStorage.setItem(SEARCH_LOB_KEY, selectedLob);
    localStorage.setItem(SEARCH_TYPE_KEY, 'lob');

    onSearch({
      state: selectedState,
      lineOfBusiness: selectedLob,
      searchType: 'searchByLob'
    });
  };

  const applyQuickSearch = (patch) => {
    const nextState = patch.state !== undefined ? patch.state : selectedState;
    const nextLob = patch.lineOfBusiness !== undefined ? patch.lineOfBusiness : selectedLob;

    setSelectedState(nextState || null);
    setSelectedLob(nextLob || '');

    if (nextState) localStorage.setItem(SEARCH_STATE_KEY, nextState);
    else localStorage.removeItem(SEARCH_STATE_KEY);
    if (nextLob) localStorage.setItem(SEARCH_LOB_KEY, nextLob);
    else localStorage.removeItem(SEARCH_LOB_KEY);
    localStorage.setItem(SEARCH_TYPE_KEY, 'lob');

    onSearch({
      state: nextState || null,
      lineOfBusiness: nextLob || '',
      searchType: 'searchByLob',
    });
  };

  const urlState = searchParams.get('state');
  const urlLob = searchParams.get('line_of_business');

  const closeQuickModal = () => {
    setQuickModal(null);
    setModalFilter('');
  };

  const modalItems = useMemo(() => {
    if (quickModal === 'states') {
      return statesList.map(({ value }) => value);
    }
    if (quickModal === 'lob') {
      return allLobList;
    }
    return [];
  }, [quickModal, statesList, allLobList]);

  const modalFindTerm = modalFilter.trim();
  const modalItemMatchesFind = (name) => {
    if (!modalFindTerm) return true;
    return name.toLowerCase().includes(modalFindTerm.toLowerCase());
  };

  const pickFromModal = (value) => {
    if (quickModal === 'states') {
      applyQuickSearch({ state: urlState === value ? null : value });
    } else if (quickModal === 'lob') {
      applyQuickSearch({ lineOfBusiness: urlLob === value ? '' : value });
    }
    closeQuickModal();
  };

  const clearAllQuickFilters = () => {
    applyQuickSearch({ state: null, lineOfBusiness: '' });
  };

  const hasQuickFilters = Boolean(urlState || urlLob);

  const quickModalTitle =
    quickModal === 'states' ? 'Browse states' : quickModal === 'lob' ? 'Browse lines of business' : '';

  return (
    <div className="search-section">
      <div className="search-content">
        {showTitle && (
          <div className="search-header search-header--brand">
            <h1 className="search-hero-title">
              <span className="search-hero-title-main">{hero.line1}</span>
              <span className="search-hero-title-sub">{hero.line2}</span>
            </h1>
          </div>
        )}

        <div className="search-panel" role="search" aria-label="Carrier search">
          <div className="search-panel__intro">
            <h2 className="search-panel__title">Market search</h2>
            <p className="search-panel__subtitle">
              Filter carriers by state and line of business
            </p>
          </div>

          <div className="search-panel__fields">
            <Row gutter={[16, 20]} align="bottom">
              <Col xs={24} md={9} lg={10}>
                <label className="search-field-label" htmlFor="search-state">
                  State
                </label>
                <Select
                  id="search-state"
                  size="large"
                  className="search-field-control"
                  placeholder="Select state"
                  options={statesList}
                  onChange={setSelectedState}
                  value={selectedState}
                  allowClear
                />
              </Col>
              <Col xs={24} md={9} lg={10}>
                <label className="search-field-label" htmlFor="search-lob">
                  Line of business
                </label>
                <AutoComplete
                  id="search-lob"
                  size="large"
                  className="search-field-control search-field-control--lob"
                  placeholder="e.g. Commercial Auto, Homeowners"
                  options={lobOptions}
                  onSearch={fetchLobOptions}
                  onChange={setSelectedLob}
                  value={selectedLob}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                />
              </Col>
              <Col xs={24} md={6} lg={4}>
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  className="search-panel__submit"
                  block
                >
                  Search
                </Button>
              </Col>
            </Row>
          </div>

          {showQuickAccess && (statesList.length > 0 || allLobList.length > 0) && (
            <div className="search-quick-access">
              <span className="search-quick-access__hint">Quick access</span>
              <div className="search-quick-access__actions">
                {statesList.length > 0 && (
                  <Button
                    type="default"
                    size="small"
                    icon={<EnvironmentOutlined />}
                    className="search-quick-access__btn"
                    onClick={() => setQuickModal('states')}
                  >
                    Browse states
                  </Button>
                )}
                {allLobList.length > 0 && (
                  <Button
                    type="default"
                    size="small"
                    icon={<AppstoreOutlined />}
                    className="search-quick-access__btn"
                    onClick={() => setQuickModal('lob')}
                  >
                    Browse lines of business
                  </Button>
                )}
                {hasQuickFilters && (
                  <Button
                    type="link"
                    size="small"
                    className="search-quick-access__clear-all"
                    onClick={clearAllQuickFilters}
                  >
                    Clear all
                  </Button>
                )}
              </div>
              {hasQuickFilters && (
                <span className="search-quick-access__active">
                  {urlState && <span className="search-quick-access__active-item">{urlState}</span>}
                  {urlState && urlLob && ' · '}
                  {urlLob && <span className="search-quick-access__active-item">{urlLob}</span>}
                </span>
              )}
            </div>
          )}
        </div>

        <Modal
          title={quickModalTitle}
          open={Boolean(quickModal)}
          onCancel={closeQuickModal}
          footer={null}
          width={640}
          destroyOnClose
          className="search-quick-modal"
        >
          <div className="search-quick-modal__find">
            <label className="search-quick-modal__find-label" htmlFor="search-quick-modal-find">
              Find in list
            </label>
            <div
              className={`search-quick-modal__find-field${
                modalFindTerm ? ' search-quick-modal__find-field--active' : ''
              }`}
            >
              <SearchOutlined className="search-quick-modal__find-icon" aria-hidden />
              <Input
                id="search-quick-modal-find"
                allowClear
                bordered={false}
                placeholder={
                  quickModal === 'states'
                    ? 'Highlight states…'
                    : 'Highlight lines of business…'
                }
                value={modalFilter}
                onChange={(e) => setModalFilter(e.target.value)}
                className="search-quick-modal__find-input"
              />
            </div>
          </div>
          <div className="search-quick-modal__grid" role="listbox">
            {modalItems.length === 0 ? (
              <p className="search-quick-modal__empty">Nothing to show.</p>
            ) : (
              modalItems.map((name) => {
                const active =
                  (quickModal === 'states' && urlState === name) ||
                  (quickModal === 'lob' && urlLob === name);
                const matchesFind = modalItemMatchesFind(name);
                return (
                  <button
                    key={name}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`search-quick-chip search-quick-modal__chip${active ? ' search-quick-chip--active' : ''}${modalFindTerm && !matchesFind ? ' search-quick-modal__chip--dim' : ''}`}
                    onClick={() => pickFromModal(name)}
                  >
                    {renderTextWithSearchHighlight(name, modalFindTerm, 'page-find-highlight')}
                  </button>
                );
              })
            )}
          </div>
          {(urlState || urlLob) && quickModal === 'states' && urlState && (
            <Button type="link" className="search-quick-modal__clear" onClick={() => { applyQuickSearch({ state: null }); closeQuickModal(); }}>
              Clear state filter
            </Button>
          )}
          {(urlState || urlLob) && quickModal === 'lob' && urlLob && (
            <Button type="link" className="search-quick-modal__clear" onClick={() => { applyQuickSearch({ lineOfBusiness: '' }); closeQuickModal(); }}>
              Clear line of business filter
            </Button>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default SearchBar;
